/**
 * Upload Job Store
 * Owns the in-flight recording upload + transcription-poll job so it survives
 * navigation away from the recording page. Non-persisted: it holds an
 * AbortController and references the encrypted audio, neither of which is
 * JSON-serializable. Crash recovery of the encrypted bytes stays in IndexedDB
 * (recordingStorage) + the uploadRetryWorker safety net.
 */

import { create } from 'zustand';
import { recordingService, CreateRecordingDto } from '@/services/recording';
import { recordingStorage } from '@/services/recordingStorage';
import type { StoredRecording } from '@/services/recordingStorage';
import { withRetry, RetryConfigs, classifyUploadError, UploadErrorCode, isFinalUploadRefusal, VISIT_NOT_ACTIVE_MESSAGE, VISIT_ALREADY_RECORDED_MESSAGE } from '@/utils/retryUtils';

export type UploadPhase =
  | 'creating'
  | 'uploading'
  | 'transcribing'
  | 'complete'
  | 'failed'
  | 'cancelled';

export interface UploadTranscriptionProgress {
  stage: string;
  progress: number;
  elapsedTime: number;
  estimatedTotalTime: number;
  processingSpeedRatio?: number;
}

export interface UploadJob {
  key: string;                 // recordingId, or the temp IndexedDB id before create resolves
  recordingId: string | null;  // server id; null until createRecording resolves
  storageId?: string;          // encryptedRecording.id, for IndexedDB cleanup on success
  appointmentId?: string;      // navigation context captured at start (page may unmount)
  encounterId?: string;
  isEncounterMode: boolean;
  patientName?: string;        // toast label only, never sent anywhere
  encounterDate?: string;      // display label only, never sent anywhere
  encounterTime?: string;      // display label only, never sent anywhere
  phase: UploadPhase;
  uploadProgress: number;      // 0-100
  transcription: UploadTranscriptionProgress | null;
  error: string | null;
  startedAt: number;
  navigated: boolean;          // guards the one-shot post-complete auto-navigate on the page
}

export interface RunUploadArgs {
  encryptedRecording: StoredRecording;
  recordingTime: number;
  isEncounterMode: boolean;
  appointmentId?: string;
  encounterId?: string;
  patientName?: string;
  encounterDate?: string;
  encounterTime?: string;
}

interface UploadJobState {
  jobs: Record<string, UploadJob>;
  runUpload: (args: RunUploadArgs) => Promise<void>;
  updateProgress: (key: string, uploadProgress: number) => void;
  updateTranscription: (key: string, transcription: UploadTranscriptionProgress) => void;
  cancel: (key: string) => void;
  dismiss: (key: string) => void;
  markNavigated: (key: string) => void;
  getJob: (key: string) => UploadJob | null;
  hasActiveJob: () => boolean;
}

// Non-reactive side channel: AbortController per job key. Never in the snapshot.
const controllers = new Map<string, AbortController>();

const ACTIVE_PHASES: UploadPhase[] = ['creating', 'uploading', 'transcribing'];

const isCancelError = (err: unknown): boolean => {
  const e = err as { name?: string; code?: string } | undefined;
  return e?.name === 'AbortError' || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
};

// Best-effort IndexedDB status write. The encrypted bytes stay in IndexedDB until a
// successful upload, so mirroring the job's interim/terminal status onto the record keeps
// the failed-uploads banner and the uploadRetryWorker able to see and resume it.
const markStorageStatus = async (
  id: string,
  status: StoredRecording['uploadStatus'],
  message?: string
): Promise<void> => {
  try {
    await (message === undefined
      ? recordingStorage.updateRecordingStatus(id, status)
      : recordingStorage.updateRecordingStatus(id, status, message));
  } catch (e) {
    console.warn('[UploadJob] Failed to update IndexedDB status', e);
  }
};

const messageForError = (err: unknown): string => {
  const error = err instanceof Error ? err : new Error(String(err));
  switch (classifyUploadError(error)) {
    case UploadErrorCode.VISIT_NOT_ACTIVE:
      return `${VISIT_NOT_ACTIVE_MESSAGE}. The recording is kept on this device.`;
    case UploadErrorCode.VISIT_ALREADY_RECORDED:
      return `${VISIT_ALREADY_RECORDED_MESSAGE}. The recording is kept on this device.`;
    case UploadErrorCode.RECORDING_NOT_ALLOWED:
      return 'Only providers can record visits in this practice. The recording is kept on this device; ask the provider to record or upload it.';
    case UploadErrorCode.SUBSCRIPTION_REQUIRED:
      return 'Your organization does not have an active subscription. Please contact your administrator.';
    case UploadErrorCode.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again.';
    case UploadErrorCode.SERVER_ERROR:
      return 'Server error. Please try again in a few minutes.';
    case UploadErrorCode.TIMEOUT:
      return 'Request timed out. Please try again.';
    default:
      return `Upload failed: ${error.message || 'Unknown error'}`;
  }
};

export const useUploadJobStore = create<UploadJobState>()((set, get) => {
  const patch = (key: string, partial: Partial<UploadJob>) => {
    const job = get().jobs[key];
    if (!job) return;
    set({ jobs: { ...get().jobs, [key]: { ...job, ...partial } } });
  };

  return {
    jobs: {},

    updateProgress: (key, uploadProgress) => patch(key, { uploadProgress }),
    updateTranscription: (key, transcription) => patch(key, { transcription }),
    markNavigated: (key) => patch(key, { navigated: true }),

    getJob: (key) => get().jobs[key] ?? null,

    hasActiveJob: () => Object.values(get().jobs).some((j) => ACTIVE_PHASES.includes(j.phase)),

    cancel: (key) => {
      // Only cancel a still-active job. A confirmation dialog can sit open long enough for the
      // job to finish (or already be gone), and cancelling then would clobber a terminal state
      // (e.g. flip 'complete' to 'cancelled', hiding the finished note).
      const job = get().jobs[key];
      if (!job || !ACTIVE_PHASES.includes(job.phase)) return;
      controllers.get(key)?.abort();
      controllers.delete(key);
      patch(key, { phase: 'cancelled' });
    },

    dismiss: (key) => {
      controllers.delete(key);
      const { [key]: _removed, ...rest } = get().jobs;
      set({ jobs: rest });
    },

    runUpload: async (args) => {
      const { encryptedRecording, recordingTime, isEncounterMode, appointmentId, encounterId, patientName, encounterDate, encounterTime } = args;
      const tempKey = encryptedRecording.id;
      let key = tempKey;

      const controller = new AbortController();
      controllers.set(tempKey, controller);

      set({
        jobs: {
          ...get().jobs,
          [tempKey]: {
            key: tempKey,
            recordingId: null,
            storageId: encryptedRecording.id,
            appointmentId,
            encounterId,
            isEncounterMode,
            patientName,
            encounterDate,
            encounterTime,
            phase: 'creating',
            uploadProgress: 0,
            transcription: null,
            error: null,
            startedAt: Date.now(),
            navigated: false,
          },
        },
      });

      try {
        const dto: CreateRecordingDto = {
          appointmentId: (!isEncounterMode ? appointmentId : undefined) || undefined,
          encryptionMetadata: encryptedRecording.encryptionMetadata,
          recordingMetadata: {
            duration: encryptedRecording.recordingMetadata.duration || recordingTime,
            size: encryptedRecording.recordingMetadata.size || encryptedRecording.encryptedAudio.byteLength,
            mimeType: encryptedRecording.recordingMetadata.mimeType,
            deviceInfo: encryptedRecording.recordingMetadata.deviceInfo,
          },
          ...(isEncounterMode && encounterId
            ? { encounterId, source: 'encounter' as const }
            : { source: 'appointment' as const }),
        };

        const createResult = await withRetry(() => recordingService.createRecording(dto), RetryConfigs.upload);
        if (!createResult.success || !createResult.data) {
          throw createResult.error || new Error('Failed to create recording');
        }

        const recordingId = createResult.data.recording.id;

        // createRecording is not abortable, so a cancel during 'creating' only aborts the
        // controller. Do not resurrect the job to 'uploading'; clean up the orphaned server
        // recording and stop before uploading.
        if (controller.signal.aborted) {
          patch(tempKey, { phase: 'cancelled' });
          controllers.delete(tempKey);
          try {
            await recordingService.deleteRecording(recordingId);
          } catch (cleanupError) {
            console.warn('[UploadJob] Failed to delete orphaned recording after cancel:', cleanupError);
          }
          return;
        }

        // Re-key from the temp id to the server recording id, moving the job and its controller atomically.
        const current = get().jobs[tempKey];
        const { [tempKey]: _dropped, ...rest } = get().jobs;
        controllers.set(recordingId, controller);
        controllers.delete(tempKey);
        key = recordingId;
        set({
          jobs: {
            ...rest,
            [recordingId]: { ...current, key: recordingId, recordingId, phase: 'uploading' },
          },
        });

        // Persist an 'uploading' marker so a crash/reload mid-upload leaves a recoverable
        // record (the retry worker reconciles orphaned 'uploading' records on next start).
        void markStorageStatus(encryptedRecording.id, 'uploading');

        const uploadOptions = {
          onUploadProgress: (progressEvent: { progress: number }) => {
            get().updateProgress(key, Math.round(progressEvent.progress * 100));
          },
          signal: controller.signal,
        };

        const uploadResult = await withRetry(
          () => recordingService.uploadRecording(recordingId, encryptedRecording.encryptedAudio, uploadOptions),
          RetryConfigs.upload
        );

        if (!uploadResult.success) {
          if (uploadResult.error && isCancelError(uploadResult.error)) {
            throw uploadResult.error;
          }
          if (uploadResult.error?.message.includes('already uploaded')) {
            const reuploadResult = await withRetry(
              () => recordingService.reuploadRecording(recordingId, encryptedRecording.encryptedAudio, uploadOptions),
              RetryConfigs.upload
            );
            if (!reuploadResult.success) {
              throw reuploadResult.error || new Error('Failed to re-upload recording');
            }
          } else {
            throw uploadResult.error || new Error('Failed to upload recording');
          }
        }

        get().updateProgress(key, 100);
        patch(key, { phase: 'transcribing' });

        const audioDuration = encryptedRecording.recordingMetadata.duration || recordingTime;
        const status = await recordingService.pollRecordingStatusSmart(
          recordingId,
          audioDuration,
          (progress) => get().updateTranscription(key, progress),
          10,
          controller.signal
        );

        if (status.status === 'transcribed') {
          patch(key, {
            phase: 'complete',
            transcription: { stage: 'complete', progress: 100, elapsedTime: 0, estimatedTotalTime: 0 },
          });
          try {
            if (encryptedRecording.id) {
              await recordingStorage.deleteRecording(encryptedRecording.id);
            }
          } catch (cleanupError) {
            console.warn('[UploadJob] Cleanup warning:', cleanupError);
          }
        } else {
          const failureMessage = status.error || 'Transcription failed. The recording was saved - you can retry later.';
          patch(key, { phase: 'failed', error: failureMessage });
          await markStorageStatus(encryptedRecording.id, 'failed', failureMessage);
        }

        controllers.delete(key);
      } catch (err) {
        controllers.delete(key);
        if (isCancelError(err)) {
          patch(key, { phase: 'cancelled' });
          return;
        }
        const failureMessage = messageForError(err);
        patch(key, { phase: 'failed', error: failureMessage });
        // A role refusal (AMP-136) or a cancelled/no-show visit is final: keep the audio but leave
        // it out of automatic retries.
        await markStorageStatus(encryptedRecording.id, isFinalUploadRefusal(err) ? 'needs_attention' : 'failed', failureMessage);
      }
    },
  };
});
